import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  WriteBatch,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Material,
  Category,
  Process,
  LaborRate,
  Vinyl,
  RollToRollPrinting,
  CompanySettings,
} from '../types';

/**
 * Initialize default company settings
 * Run this once after creating your Firebase project
 */
export const initializeCompanySettings = async (): Promise<void> => {
  const companySettingsRef = doc(db, 'companySettings', 'redmaxevents');

  const defaultSettings: CompanySettings = {
    id: 'redmaxevents',
    branding: {
      companyName: 'Redmax Events',
      logoUrl: '',
      logoAlt: 'Redmax Events Logo',
      faviconUrl: '',
      tagline: 'Fabrication & Printing Solutions',
      loginPageTitle: 'Redmax Quoting System',
      loginPageSubtitle: 'Sign In',
      loginSignupText: 'Create Account',
    },
    colors: {
      // Header/Navigation
      headerBackground: '#667eea',
      headerText: '#ffffff',
      headerButton: '#764ba2',

      // Main Content
      mainBackground: '#ffffff',
      mainText: '#1f2937',
      secondaryText: '#6b7280',

      // Cards/Windows
      cardBackground: '#ffffff',
      cardText: '#1f2937',

      // Buttons
      primaryButton: '#667eea',
      primaryButtonText: '#ffffff',
      secondaryButton: '#f3f4f6',
      secondaryButtonText: '#1f2937',

      // UI Elements
      border: '#e5e7eb',
      accent: '#f59e0b',

      // Status Colors
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      headingSize: 24,
      bodySize: 16,
      fontScale: {
        h1: 32,
        h2: 24,
        h3: 20,
        body: 14,
      },
    },
    quotes: {
      pdfHeaderText: 'REDMAX EVENTS',
      pdfFooterText: 'Thank you for your business!',
      quotePrefix: 'RME-',
      showMarkupDetails: true,
      currencySymbol: '$',
      defaultTerms: 'Payment due upon invoice. 50% deposit required to start work.',
      notesTemplate: '',
      defaultTaxRate: 0,
    },
    email: {
      senderEmail: 'quotes@redmaxevents.com',
      senderName: 'Redmax Events Quotes',
      quoteSubjectLine: 'Your Quote - {projectName} - {quoteNumber}',
      quoteEmailTemplate: '<p>Hi {clientName},</p><p>Please find your quote attached.</p>',
    },
    updatedAt: new Date(),
  };

  await setDoc(companySettingsRef, defaultSettings);
  console.log('Company settings initialized');
};

/**
 * Initialize default categories
 */
export const initializeDefaultCategories = async (): Promise<void> => {
  const categories: Omit<Category, 'id'>[] = [
    { name: 'Wood', parentCategoryId: null, order: 1 },
    { name: 'PVC', parentCategoryId: null, order: 2 },
    { name: 'Acrylic', parentCategoryId: null, order: 3 },
    { name: 'Composite', parentCategoryId: null, order: 4 },
    { name: 'Coroplast', parentCategoryId: null, order: 5 },
    { name: 'Diebond', parentCategoryId: null, order: 6 },
    { name: 'Durabond', parentCategoryId: null, order: 7 },
    { name: 'Foam Board', parentCategoryId: null, order: 8 },
    { name: 'Ultraboard', parentCategoryId: null, order: 9 },
  ];

  const batch = writeBatch(db);
  const categoriesRef = collection(db, 'categories');

  for (const category of categories) {
    const docRef = doc(categoriesRef);
    batch.set(docRef, category);
  }

  await batch.commit();
  console.log('Default categories initialized');
};

/**
 * Initialize default processes/modifiers
 */
export const initializeDefaultProcesses = async (): Promise<void> => {
  const processes: Omit<Process, 'id'>[] = [
    {
      name: 'Paint',
      description: 'Standard paint finish',
      flatRateCost: 25,
      isActive: true,
      order: 1,
    },
    {
      name: 'High Gloss Paint',
      description: 'High gloss paint finish',
      flatRateCost: 35,
      isActive: true,
      order: 2,
    },
    {
      name: 'CNC',
      description: 'CNC cutting and routing',
      flatRateCost: 100,
      sqftCost: 2,
      isActive: true,
      order: 3,
    },
    {
      name: 'Flatbed Printing',
      description: 'Direct printing on flatbed printer',
      sqftCost: 5,
      isActive: true,
      order: 4,
    },
    {
      name: 'Roll to Roll Printing',
      description: 'Continuous roll printing',
      sqftCost: 3.5,
      isActive: true,
      order: 5,
    },
    {
      name: 'Flatbed with Reg Marks',
      description: 'Flatbed printing with registration marks for Colex CNC',
      sqftCost: 8,
      isActive: true,
      order: 6,
    },
    {
      name: 'Roll to Roll with Reg Marks',
      description: 'Roll printing with registration marks',
      sqftCost: 6,
      isActive: true,
      order: 7,
    },
    {
      name: 'Laser Cutting',
      description: 'Laser cutting and engraving cuts',
      flatRateCost: 50,
      sqftCost: 1.5,
      isActive: true,
      order: 8,
    },
    {
      name: 'Laser Engraving',
      description: 'Laser engraving detail work',
      sqftCost: 4,
      isActive: true,
      order: 9,
    },
  ];

  const batch = writeBatch(db);
  const processesRef = collection(db, 'processes');

  for (const process of processes) {
    const docRef = doc(processesRef);
    batch.set(docRef, process);
  }

  await batch.commit();
  console.log('Default processes initialized');
};

/**
 * Initialize default labor rates
 */
export const initializeDefaultLaborRates = async (): Promise<void> => {
  const laborRates: Omit<LaborRate, 'id'>[] = [
    {
      name: 'General Labor',
      description: 'Standard labor work',
      rateType: 'hourly',
      hourlyRate: 50,
      markupFactor: 2, // 100% markup
      isActive: true,
      order: 1,
    },
    {
      name: 'Installation',
      description: 'On-site installation',
      rateType: 'hourly',
      hourlyRate: 75,
      markupFactor: 2,
      isActive: true,
      order: 2,
    },
    {
      name: 'Design Work',
      description: 'Design and layout',
      rateType: 'per_sqft',
      perSqftRate: 2.5,
      markupFactor: 2,
      isActive: true,
      order: 3,
    },
  ];

  const batch = writeBatch(db);
  const laborRatesRef = collection(db, 'laborRates');

  for (const rate of laborRates) {
    const docRef = doc(laborRatesRef);
    batch.set(docRef, rate);
  }

  await batch.commit();
  console.log('Default labor rates initialized');
};

/**
 * Initialize default vinyl types
 */
export const initializeDefaultVinyls = async (): Promise<void> => {
  const vinyls: Omit<Vinyl, 'id'>[] = [
    {
      name: 'White Vinyl',
      color: 'white',
      type: 'standard',
      basePrice: 2.5,
      markupFactor: 2,
      width: 54,
      isActive: true,
      order: 1,
    },
    {
      name: 'Clear Vinyl',
      color: 'clear',
      type: 'standard',
      basePrice: 3,
      markupFactor: 2,
      width: 54,
      isActive: true,
      order: 2,
    },
    {
      name: 'Premium White Vinyl',
      color: 'white',
      type: 'premium',
      basePrice: 4,
      markupFactor: 2,
      width: 54,
      isActive: true,
      order: 3,
    },
  ];

  const batch = writeBatch(db);
  const vinylsRef = collection(db, 'vinyl');

  for (const vinyl of vinyls) {
    const docRef = doc(vinylsRef);
    batch.set(docRef, vinyl);
  }

  await batch.commit();
  console.log('Default vinyl types initialized');
};

/**
 * Initialize default roll-to-roll printing types
 */
export const initializeDefaultPrintingTypes = async (): Promise<void> => {
  const printingTypes: Omit<RollToRollPrinting, 'id'>[] = [
    {
      name: 'Standard Roll-to-Roll',
      description: 'Standard 4-color printing',
      pricePerSqft: 3,
      markupFactor: 2,
      minWidth: 24,
      maxWidth: 60,
      isActive: true,
      order: 1,
    },
    {
      name: 'Premium Full Color',
      description: 'High-quality color printing',
      pricePerSqft: 5,
      markupFactor: 2,
      minWidth: 24,
      maxWidth: 60,
      isActive: true,
      order: 2,
    },
  ];

  const batch = writeBatch(db);
  const printingRef = collection(db, 'rollToRollPrinting');

  for (const printing of printingTypes) {
    const docRef = doc(printingRef);
    batch.set(docRef, printing);
  }

  await batch.commit();
  console.log('Default printing types initialized');
};

/**
 * Run all initialization functions
 * Call this once from your admin setup page
 */
export const initializeDatabase = async (): Promise<void> => {
  try {
    console.log('Starting database initialization...');
    await initializeCompanySettings();
    await initializeDefaultCategories();
    await initializeDefaultProcesses();
    await initializeDefaultLaborRates();
    await initializeDefaultVinyls();
    await initializeDefaultPrintingTypes();
    console.log('Database initialization complete!');
  } catch (error) {
    console.error('Error initializing database:', error);
    throw error;
  }
};
